/**
 * The a*b* gamut of an exploration's measured samples, with the sub-gamut
 * of the valid ones (the Results page's "Valid samples" tab, ORC-1163).
 *
 * Every measured sample is a dot in its own display colour (from L*a*b*);
 * the convex hull of all of them is the full gamut (filled), the convex
 * hull of the valid ones the sub-gamut (dashed). Only the samples on either
 * hull are drawn as dots (the interior gets crowded); dots that fail a filter
 * are faded. The axes span ``rangeSamples`` (all samples of the exploration,
 * i.e. the extent at the last iteration) so the plot does not jump when the
 * samples shown change under the iteration slider.
 * Plain SVG, so it renders without canvas or WebGL.
 */
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { labToDisplayRgb } from "../helper/colorConversion";

const GAMUT_FILL = "rgba(59, 130, 246, 0.12)";
const GAMUT_STROKE = "#3b82f6";
const VALID_STROKE = "#334155";
const AXIS = "#94a3b8";
const GRID = "#e2e8f0";
const AXIS_PAD = 5;

/** Convex hull (monotone chain) of ``[{a, b}]`` points, counter-clockwise;
 *  fewer than three distinct points come back as they are. */
export function convexHull(points) {
    const distinct = Array.from(new Map(points.map((p) => [`${p.a},${p.b}`, { a: p.a, b: p.b }])).values())
        .sort((p, q) => (p.a - q.a) || (p.b - q.b));
    if (distinct.length < 3) return distinct;
    const cross = (o, p, q) => (p.a - o.a) * (q.b - o.b) - (p.b - o.b) * (q.a - o.a);
    const lower = [];
    distinct.forEach((p) => {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    });
    const upper = [];
    [...distinct].reverse().forEach((p) => {
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    });
    return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/** Tick positions at multiples of the step that cover [min, max]. */
function ticks(min, max, step) {
    const out = [];
    for (let v = Math.ceil(min / step) * step; v <= max; v += step) out.push(v);
    return out;
}

const toPoints = (samples) => (samples || [])
    .filter((s) => Number.isFinite(Number(s?.a_star)) && Number.isFinite(Number(s?.b_star)))
    .map((s) => ({
        a: Number(s.a_star), b: Number(s.b_star), l: Number(s.l_star), valid: Boolean(s.valid),
    }));

/** The axis range covering the values with a margin, rounded out to multiples of 5. */
export function axisRange(values) {
    const min = Math.min(...values) - AXIS_PAD;
    const max = Math.max(...values) + AXIS_PAD;
    return [Math.floor(min / AXIS_PAD) * AXIS_PAD, Math.ceil(max / AXIS_PAD) * AXIS_PAD];
}

export default function ValidSamplesGamut({ samples, rangeSamples, width = 520, height = 540 }) {
    const points = toPoints(samples);
    if (points.length === 0) return null;
    const rangePoints = rangeSamples ? toPoints(rangeSamples) : points;
    const extent = rangePoints.length ? rangePoints : points;

    const margin = { top: 12, right: 16, bottom: 40, left: 48 };
    const [aMin, aMax] = axisRange(extent.map((p) => p.a));
    const [bMin, bMax] = axisRange(extent.map((p) => p.b));
    const tickStep = Math.max(aMax - aMin, bMax - bMin) > 150 ? 25 : 10;
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const x = (a) => margin.left + ((a - aMin) / (aMax - aMin)) * plotWidth;
    const y = (b) => margin.top + ((bMax - b) / (bMax - bMin)) * plotHeight;
    const polygon = (hull) => hull.map((p) => `${x(p.a).toFixed(1)},${y(p.b).toFixed(1)}`).join(" ");

    const allHull = convexHull(points);
    const validPoints = points.filter((p) => p.valid);
    const validHull = convexHull(validPoints);
    // Only the samples that shape a hull are drawn.
    const onHull = new Set([...allHull, ...validHull].map((p) => `${p.a},${p.b}`));
    const drawn = points.filter((p) => onHull.has(`${p.a},${p.b}`));

    return (
        <Stack spacing={1} alignItems="center" data-testid="valid-samples-gamut">
            <Box component="svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`}
                sx={{ maxWidth: "100%", height: "auto" }} role="img" aria-label="Gamut of the measured samples">
                {ticks(aMin, aMax, tickStep).map((v) => (
                    <g key={`a${v}`}>
                        <line x1={x(v)} x2={x(v)} y1={margin.top} y2={margin.top + plotHeight} stroke={GRID} strokeDasharray="3 3" />
                        <text x={x(v)} y={margin.top + plotHeight + 16} fontSize={11} fill={AXIS} textAnchor="middle">{v}</text>
                    </g>
                ))}
                {ticks(bMin, bMax, tickStep).map((v) => (
                    <g key={`b${v}`}>
                        <line x1={margin.left} x2={margin.left + plotWidth} y1={y(v)} y2={y(v)} stroke={GRID} strokeDasharray="3 3" />
                        <text x={margin.left - 6} y={y(v) + 4} fontSize={11} fill={AXIS} textAnchor="end">{v}</text>
                    </g>
                ))}
                <line x1={margin.left} x2={margin.left + plotWidth} y1={margin.top + plotHeight} y2={margin.top + plotHeight} stroke={AXIS} />
                <line x1={margin.left} x2={margin.left} y1={margin.top} y2={margin.top + plotHeight} stroke={AXIS} />
                <text x={margin.left + plotWidth / 2} y={height - 6} fontSize={12} fill="#64748b" textAnchor="middle">a*</text>
                <text x={14} y={margin.top + plotHeight / 2} fontSize={12} fill="#64748b" textAnchor="middle"
                    transform={`rotate(-90 14 ${margin.top + plotHeight / 2})`}>b*</text>
                {allHull.length >= 3 ? (
                    <polygon data-testid="gamut-all" points={polygon(allHull)} fill={GAMUT_FILL} stroke={GAMUT_STROKE} strokeWidth={1.5} />
                ) : null}
                {validHull.length >= 3 ? (
                    <polygon data-testid="gamut-valid" points={polygon(validHull)} fill="none" stroke={VALID_STROKE} strokeWidth={1.5} strokeDasharray="6 4" />
                ) : null}
                {drawn.map((p, index) => (
                    <circle
                        key={index}
                        data-valid={p.valid ? "true" : "false"}
                        cx={x(p.a)}
                        cy={y(p.b)}
                        r={p.valid ? 5 : 4}
                        fill={labToDisplayRgb([p.l, p.a, p.b]) || "#999"}
                        stroke={p.valid ? VALID_STROKE : "#ffffff"}
                        strokeWidth={p.valid ? 1.5 : 1}
                        opacity={p.valid ? 1 : 0.4}
                    />
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
                Filled: gamut of all measured samples. Dashed: gamut of the valid ones. Dots: the samples on either hull; faded ones fail a filter.
            </Typography>
        </Stack>
    );
}
